import React, { useState, useMemo } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Search, 
  Download, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  Filter,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  EyeOff,
  Settings,
  FileSpreadsheet
} from 'lucide-react';
import * as XLSX from 'xlsx';
import ActionMenu from './ActionMenu';

const DataTable = ({
  data = [],
  columns = [],
  title = "Data Table",
  searchable = true,
  sortable = true,
  exportable = true,
  showFilterButton = true,
  pagination = true,
  pageSize = 10,
  showActions = true,
  showCheckboxes = false,
  onRowClick = null,
  onEdit = null,
  onDelete = null,
  onView = null,
  onCopy = null,
  onRiskAnalysis = null,
  onPageChange = null,
  onSearchChange = null,
  customActions = [],
  className = "",
  searchPlaceholder = "Search...",
  emptyMessage = "No data available",
  loading = false,
  initialHiddenColumns = []
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedRows, setSelectedRows] = useState([]);
  const [tooltip, setTooltip] = useState({ show: false, text: '', x: 0, y: 0 });
  const [hiddenColumns, setHiddenColumns] = useState(initialHiddenColumns);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, right: 0 });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState({});
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);

  // Close column settings when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColumnSettings && !event.target.closest('.position-relative')) {
        setShowColumnSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnSettings]);

  // Tooltip component
  const Tooltip = ({ show, text, x, y }) => {
    if (!show) return null;
    
    return (
      <div
        style={{
          position: 'fixed',
          left: x,
          top: y - 35,
          backgroundColor: 'var(--text-primary)',
          color: 'var(--white)',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: '500',
          whiteSpace: 'nowrap',
          zIndex: 1000,
          pointerEvents: 'none',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
        }}
      >
        {text}
      </div>
    );
  };

  // Handle tooltip
  const handleTooltip = (show, text, event) => {
    if (show && event) {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltip({
        show: true,
        text,
        x: rect.left + rect.width / 2,
        y: rect.top
      });
    } else {
      setTooltip({ show: false, text: '', x: 0, y: 0 });
    }
  };

  // Handle column visibility
  const toggleColumnVisibility = (columnKey) => {
    setHiddenColumns(prev => 
      prev.includes(columnKey) 
        ? prev.filter(key => key !== columnKey)
        : [...prev, columnKey]
    );
  };

  // Get visible columns
  const visibleColumns = useMemo(() => {
    return columns.filter(column => !hiddenColumns.includes(column.key));
  }, [columns, hiddenColumns]);
  
  // Filter data based on search term and filters
  const filteredData = useMemo(() => {
    //console.log('=== DataTable filteredData calculation ===');
    //console.log('Data received:', data);
    //console.log('Data type:', typeof data);
    //console.log('Data is array:', Array.isArray(data));
    //console.log('Data length:', data?.length);
    
    if (!data || !Array.isArray(data)) {
      //console.log('No data or not array, returning empty array');
      return [];
    }
    
    let filtered = data;
    
    // Apply search term
    // If onSearchChange is provided, skip client-side filtering (server handles it)
    if (searchTerm && !onSearchChange) {
      filtered = filtered.filter(row => {
        if (!row) return false;
        return visibleColumns.some(column => {
          const value = row[column.key];
          if (value === null || value === undefined) return false;
          return value.toString().toLowerCase().includes(searchTerm.toLowerCase());
        });
      });
    }
    
    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '') {
        filtered = filtered.filter(row => {
          if (!row) return false;
          const rowValue = row[key];
          if (rowValue === null || rowValue === undefined) return false;
          if (typeof rowValue === 'string') {
            return rowValue.toLowerCase().includes(value.toLowerCase());
          }
          return rowValue === value;
        });
      }
    });
    
    //console.log('Filtered data result:', filtered);
    //console.log('Filtered data length:', filtered.length);
    //console.log('=== DataTable filteredData calculation END ===');
    
    return filtered;
  }, [data, searchTerm, visibleColumns, filters, onSearchChange]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [filteredData, sortConfig]);

  // Paginate data
  // If pagination is an object (server-side), don't paginate client-side
  // If pagination is true (client-side), paginate the data
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    if (typeof pagination === 'object' && pagination !== null) {
      // Server-side pagination - return data as-is
      return sortedData;
    }
    
    // Client-side pagination
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, rowsPerPage, pagination]);

  // Calculate total pages
  // If pagination is an object (server-side), use its pagination info
  // Otherwise, calculate from data length
  const totalPages = useMemo(() => {
    if (typeof pagination === 'object' && pagination !== null) {
      const total = pagination.total || pagination.totalItems || sortedData.length;
      const itemsPerPage = pagination.itemsPerPage || rowsPerPage;
      return pagination.totalPages || Math.ceil(total / itemsPerPage);
    }
    return Math.ceil(sortedData.length / rowsPerPage);
  }, [sortedData.length, rowsPerPage, pagination]);

  // Handle rows per page change
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  // Handle sort
  const handleSort = (key) => {
    if (!sortable) return;
    
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // If onPageChange callback is provided, call it for server-side pagination
    if (onPageChange && typeof onPageChange === 'function') {
      onPageChange(page);
    }
  };

  // Handle row selection
  const handleRowSelect = (rowId, isSelected) => {
    if (isSelected) {
      setSelectedRows(prev => [...prev, rowId]);
    } else {
      setSelectedRows(prev => prev.filter(id => id !== rowId));
    }
  };

  // Handle select all
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedRows(paginatedData.map(row => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  // Export to Excel
  const handleExport = () => {
    const exportData = selectedRows.length > 0 
      ? data.filter(row => selectedRows.includes(row.id))
      : sortedData;

    const worksheet = XLSX.utils.json_to_sheet(
      exportData.map(row => {
        const exportRow = {};
        columns.forEach(column => {
          exportRow[column.label] = row[column.key];
        });
        return exportRow;
      })
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
    
    const fileName = `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (!sortable) return null;
    
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? 
        <ArrowUp size={16} className="text-primary" /> : 
        <ArrowDown size={16} className="text-primary" />;
    }
    return <ArrowUpDown size={16} className="text-muted" />;
  };

  // Render cell content
  const renderCell = (row, column) => {
    if (!row || !column) return null;
    
    const value = row[column.key];
    
    // Render cell content
    
    // If column has a render function, use it (even for objects)
    // Support multiple patterns:
    // 1. render(value, row) - most common pattern (backward compatible)
    // 2. render(row) - when function expects full row object
    // 3. render(value) - when function expects just the value
    if (column.render) {
      try {
        const result = column.render(value, row);
        // Safety check: if render function returns an object (not a React element), handle it
        if (result && typeof result === 'object' && !React.isValidElement(result) && result.constructor === Object) {
          console.warn('Render function returned an object instead of React element:', result);
          return JSON.stringify(result);
        }
        return result;
      } catch (error) {
        console.error('Error in column render function:', error);
        return String(value || '');
      }
    }
    
    // Safety check: don't render objects directly if no render function
    if (typeof value === 'object' && value !== null) {
      console.warn('Attempted to render object in DataTable without render function:', value);
      return JSON.stringify(value);
    }
    
    if (column.type === 'badge') {
      const badgeClass = column.badgeClass ? column.badgeClass(value) : 'badge-secondary';
      return <span className={`badge ${badgeClass}`}>{String(value || '')}</span>;
    }
    
    if (column.type === 'date') {
      return new Date(value).toLocaleDateString();
    }
    
    if (column.type === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value);
    }
    
    return String(value || '');
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Tooltip show={tooltip.show} text={tooltip.text} x={tooltip.x} y={tooltip.y} />
      <div className={`card ${className}`}>
      {/* Header with Search Bar - Merged */}
      <div className="card-header" style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', padding: '16px 24px' }}>
        <div className="d-flex align-items-center justify-content-between gap-3" style={{ flexWrap: 'wrap' }}>
          {/* Title */}
          <div style={{ flexShrink: 0 }}>
            <h3 className="mb-0" style={{ 
              color: 'var(--primary-color)', 
              fontFamily: 'var(--font-family)', 
              fontWeight: '700', 
              fontSize: '20px',
              marginBottom: '4px'
            }}>
              {title}
            </h3>
            {title === 'Projects' && (
              <p style={{
                fontSize: '13px',
                color: '#6b7280',
                margin: 0,
                fontFamily: 'var(--font-family)',
                fontWeight: '400'
              }}>
                List of active and archived projects across your organization.
              </p>
            )}
          </div>
          
          {/* Search Bar - Middle */}
          {searchable && (
            <div className="d-flex align-items-center" style={{ flex: '1', minWidth: '250px', maxWidth: '400px', margin: '0 auto', marginLeft: '20px' }}>
              <span className="input-group-text" style={{ 
                borderTopRightRadius: 0, 
                borderBottomRightRadius: 0, 
                borderRight: 'none',
                padding: '6px 10px',
                marginRight: 0,
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRight: 'none',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Search size={14} style={{ color: 'var(--primary-color)' }} />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setSearchTerm(newValue);
                  // If onSearchChange callback is provided, call it for server-side search
                  if (onSearchChange && typeof onSearchChange === 'function') {
                    onSearchChange(newValue);
                  }
                }}
                style={{ 
                  borderTopLeftRadius: 0, 
                  borderBottomLeftRadius: 0, 
                  borderLeft: 'none',
                  marginLeft: 0,
                  paddingLeft: '0.75rem',
                  paddingRight: '0.75rem',
                  paddingTop: '6px',
                  paddingBottom: '6px',
                  border: '1px solid #d1d5db',
                  borderLeft: 'none',
                  height: '32px',
                  fontSize: '14px'
                }}
              />
            </div>
          )}
          
          {/* Action Buttons - Right */}
          <div className="d-flex justify-content-end datatable-header-buttons" style={{ flexShrink: 0, gap: '10px' }}>
            {exportable && (
              <button 
                className="btn btn-sm"
                style={{
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  fontWeight: '500',
                  border: '1px solid #e5e7eb',
                  color: 'var(--primary-color)',
                  backgroundColor: 'white',
                  borderRadius: '0',
                  padding: '6px 10px',
                  transition: 'all 0.2s ease',
                  fontSize: '14px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '32px',
                  marginRight: '0'
                }}
                onClick={handleExport}
                disabled={data.length === 0}
                onMouseEnter={(e) => {
                  if (!e.target.disabled) {
                    handleTooltip(true, 'Export Data', e);
                    e.target.style.backgroundColor = '#f3f4f6';
                    e.target.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.target.disabled) {
                    handleTooltip(false);
                    e.target.style.backgroundColor = 'white';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                <div style={{
                  width: '16px',
                  height: '16px',
                  background: '#217346',
                  borderRadius: '2px',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: 'bold'
                }}>
                  X
                </div>
              </button>
            )}
            {showFilterButton && (
            <button 
              className="btn-outline-cam btn-sm"
              style={{
                padding: '6px 10px',
                fontSize: '14px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '32px',
                border: '1px solid #e5e7eb',
                backgroundColor: 'white',
                color: 'var(--primary-color)',
                marginLeft: '0',
                marginRight: '0',
                transition: 'all 0.2s ease'
              }}
              onClick={() => setShowFilterModal(true)}
              onMouseEnter={(e) => {
                handleTooltip(true, 'Advanced Filters', e);
                e.target.style.backgroundColor = '#f3f4f6';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.borderColor = 'var(--primary-color)';
                e.target.style.boxShadow = '0 2px 4px rgba(70, 120, 190, 0.15)';
              }}
              onMouseLeave={(e) => {
                handleTooltip(false);
                e.target.style.backgroundColor = 'white';
                e.target.style.transform = 'translateY(0)';
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            >
              <Filter size={14} />
            </button>
            )}
            {/* Settings button temporarily hidden */}
            {false && (
              <div className="position-relative" style={{ zIndex: 10000 }}>
                <button 
                  className="btn-outline-cam btn-sm"
                  style={{
                    padding: '8px 12px',
                    fontSize: '14px'
                  }}
                  onClick={() => setShowColumnSettings(!showColumnSettings)}
                  onMouseEnter={(e) => {
                    handleTooltip(true, 'Column Settings', e);
                  }}
                  onMouseLeave={(e) => {
                    handleTooltip(false);
                  }}
                >
                  <Settings size={16} />
                </button>
              
              {/* Column Settings Dropdown */}
              {showColumnSettings && (
                <div 
                  className="position-absolute border rounded shadow-lg p-3"
                  style={{
                    top: '100%',
                    right: 0,
                    zIndex: 99999,
                    minWidth: '200px',
                    marginTop: '4px',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.4)',
                    border: '2px solid #dee2e6',
                    borderRadius: '0',
                    opacity: 1,
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <div className="mb-2">
                    <strong style={{ fontSize: '14px' }}>Show/Hide Columns</strong>
                  </div>
                  {columns.map(column => (
                    <div key={column.key} className="form-check" style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`column-${column.key}`}
                        checked={!hiddenColumns.includes(column.key)}
                        onChange={() => toggleColumnVisibility(column.key)}
                        style={{ marginRight: '8px', marginTop: '0' }}
                      />
                      <label 
                        className="form-check-label" 
                        htmlFor={`column-${column.key}`}
                        style={{ fontSize: '13px', marginBottom: '0', cursor: 'pointer' }}
                      >
                        {column.label}
                      </label>
                    </div>
                  ))}
                </div>
              )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-hover mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead className="table-light" style={{ backgroundColor: '#f9fafb' }}>
            <tr>
              {showCheckboxes && (
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedRows.length === paginatedData.length && paginatedData.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
              )}
              {visibleColumns.map((column) => {
                const isActive = sortConfig.key === column.key;
                return (
                  <th 
                    key={column.key}
                    style={{ 
                      cursor: sortable ? 'pointer' : 'default',
                      userSelect: 'none',
                      width: column.width || 'auto',
                      backgroundColor: isActive ? '#f3f4f6' : '#f9fafb',
                      transition: 'background-color 0.2s ease',
                      fontWeight: isActive ? '600' : '500',
                      padding: '12px 16px',
                      borderBottom: '2px solid #e5e7eb',
                      textAlign: column.align || 'left'
                    }}
                    onClick={() => handleSort(column.key)}
                    className={sortable ? 'sortable' : ''}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }
                    }}
                  >
                    <div className="d-flex align-items-center gap-2" style={{ justifyContent: column.align === 'right' ? 'flex-end' : column.align === 'center' ? 'center' : 'flex-start' }}>
                      {column.label}
                      {getSortIcon(column.key)}
                    </div>
                  </th>
                );
              })}
              {showActions && (
                <th style={{ width: '80px' }}>Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td 
                  colSpan={visibleColumns.length + (showCheckboxes ? 1 : 0) + (showActions ? 1 : 0)} 
                  className="text-center py-4 text-muted"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              paginatedData.map((row, index) => (
                <tr 
                  key={row.id || index}
                  style={{ 
                    cursor: onRowClick ? 'pointer' : 'default',
                    borderBottom: '1px solid #f3f4f6',
                    transition: 'background-color 0.2s ease'
                  }}
                  onClick={() => onRowClick && onRowClick(row)}
                  className={selectedRows.includes(row.id) ? 'table-active' : ''}
                  onMouseEnter={(e) => {
                    if (!selectedRows.includes(row.id)) {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selectedRows.includes(row.id)) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {showCheckboxes && (
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedRows.includes(row.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleRowSelect(row.id, e.target.checked);
                        }}
                      />
                    </td>
                  )}
                  {visibleColumns.map((column) => (
                    <td 
                      key={column.key}
                      style={{
                        padding: '14px 16px',
                        verticalAlign: 'middle',
                        borderBottom: '1px solid #f3f4f6',
                        textAlign: column.align || 'left'
                      }}
                    >
                      {renderCell(row, column)}
                    </td>
                  ))}
                  {showActions && (
                    <td>
                      <div className="d-flex justify-content-center">
                        <ActionMenu
                          actions={[
                            ...(onView ? [{ type: 'view', label: 'View Details' }] : []),
                            ...(onEdit ? [{ type: 'edit', label: 'Edit' }] : []),
                            ...(onCopy ? [{ type: 'copy', label: 'Copy Content' }] : []),
                            ...(onRiskAnalysis ? [{ type: 'risk', label: 'AI Risk Analysis' }] : []),
                            ...customActions,
                            ...(onDelete ? [{ type: 'delete', label: 'Delete' }] : [])
                          ]}
                          onAction={(action) => {
                            switch (action.type) {
                              case 'view':
                                onView && onView(row);
                                break;
                              case 'edit':
                                onEdit && onEdit(row);
                                break;
                              case 'copy':
                                onCopy && onCopy(row);
                                break;
                              case 'risk':
                                onRiskAnalysis && onRiskAnalysis(row);
                                break;
                              case 'delete':
                                onDelete && onDelete(row);
                                break;
                              default:
                                // Handle custom actions
                                if (action.onClick) {
                                  action.onClick(row);
                                }
                                break;
                            }
                          }}
                          size="sm"
                          variant="outline"
                        />
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="card-footer" style={{ 
          backgroundColor: 'white', 
          borderTop: '1px solid #e5e7eb',
          padding: '12px 24px',
          minHeight: 'auto'
        }}>
          <div className="d-flex justify-content-between align-items-center" style={{ flexWrap: 'wrap', gap: '12px' }}>
            <div className="d-flex align-items-center gap-3" style={{ flexWrap: 'wrap' }}>
              <div style={{ fontSize: '14px', color: '#6b7280', fontFamily: 'var(--font-family)' }}>
                Page {typeof pagination === 'object' && pagination !== null ? (pagination.currentPage || currentPage) : currentPage} of {totalPages} ({typeof pagination === 'object' && pagination !== null ? (pagination.total || pagination.totalItems || sortedData.length) : sortedData.length} total entries)
              </div>
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: '14px', color: '#6b7280', fontFamily: 'var(--font-family)' }}>Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                  style={{
                    padding: '6px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    outline: 'none',
                    color: '#374151',
                    minWidth: '60px'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--primary-color)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(70, 120, 190, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = 'var(--primary-color)';
                  }}
                  onMouseLeave={(e) => {
                    if (document.activeElement !== e.target) {
                      e.target.style.borderColor = '#d1d5db';
                    }
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
            <nav>
              <ul className="pagination pagination-sm mb-0" style={{ margin: 0, display: 'flex', gap: '4px' }}>
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`} style={{ listStyle: 'none' }}>
                  <button 
                    className="page-link"
                    style={{
                      fontFamily: 'var(--font-family)',
                      fontWeight: '500',
                      border: '1px solid #d1d5db',
                      color: currentPage === 1 ? '#9ca3af' : 'var(--primary-color)',
                      backgroundColor: 'white',
                      borderRadius: '0',
                      padding: '6px 10px',
                      minWidth: '36px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      outline: 'none'
                    }}
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    onMouseEnter={(e) => {
                      if (!e.target.disabled) {
                        e.target.style.backgroundColor = 'var(--primary-color)';
                        e.target.style.color = 'white';
                        e.target.style.borderColor = 'var(--primary-color)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!e.target.disabled) {
                        e.target.style.backgroundColor = 'white';
                        e.target.style.color = 'var(--primary-color)';
                        e.target.style.borderColor = '#d1d5db';
                      }
                    }}
                  >
                    <ChevronLeft size={16} />
                  </button>
                </li>
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`} style={{ listStyle: 'none' }}>
                      <button 
                        className="page-link"
                        style={{
                          fontFamily: 'var(--font-family)',
                          fontWeight: currentPage === pageNum ? '600' : '500',
                          border: '1px solid #d1d5db',
                          color: currentPage === pageNum ? 'white' : 'var(--primary-color)',
                          backgroundColor: currentPage === pageNum ? 'var(--primary-color)' : 'white',
                          borderRadius: '0',
                          padding: '6px 12px',
                          minWidth: '36px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          outline: 'none'
                        }}
                        onClick={() => handlePageChange(pageNum)}
                        onMouseEnter={(e) => {
                          if (currentPage !== pageNum) {
                            e.target.style.backgroundColor = 'var(--primary-color)';
                            e.target.style.color = 'white';
                            e.target.style.borderColor = 'var(--primary-color)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentPage !== pageNum) {
                            e.target.style.backgroundColor = 'white';
                            e.target.style.color = 'var(--primary-color)';
                            e.target.style.borderColor = '#d1d5db';
                          }
                        }}
                      >
                        {pageNum}
                      </button>
                    </li>
                  );
                })}
                
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`} style={{ listStyle: 'none' }}>
                  <button 
                    className="page-link"
                    style={{
                      fontFamily: 'var(--font-family)',
                      fontWeight: '500',
                      border: '1px solid #d1d5db',
                      color: currentPage === totalPages ? '#9ca3af' : 'var(--primary-color)',
                      backgroundColor: 'white',
                      borderRadius: '0',
                      padding: '6px 10px',
                      minWidth: '36px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      outline: 'none'
                    }}
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    onMouseEnter={(e) => {
                      if (!e.target.disabled) {
                        e.target.style.backgroundColor = 'var(--primary-color)';
                        e.target.style.color = 'white';
                        e.target.style.borderColor = 'var(--primary-color)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!e.target.disabled) {
                        e.target.style.backgroundColor = 'white';
                        e.target.style.color = 'var(--primary-color)';
                        e.target.style.borderColor = '#d1d5db';
                      }
                    }}
                  >
                    <ChevronRight size={16} />
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
      </div>

      {/* Filter Modal */}
      {showFilterModal && (
        <div 
          className="position-fixed d-flex align-items-center justify-content-center"
          style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 100000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowFilterModal(false)}
        >
          <div 
            className="rounded shadow-lg p-4"
            style={{ 
              minWidth: '450px', 
              maxWidth: '500px',
              backgroundColor: '#ffffff',
              border: '2px solid #dee2e6',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
              opacity: 1
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0" style={{ fontFamily: 'var(--font-family)' }}>Filter Assets</h5>
              <button 
                className="btn-close" 
                onClick={() => setShowFilterModal(false)}
                style={{ fontSize: '12px' }}
              ></button>
            </div>
            
            <div className="mb-3">
              {visibleColumns.filter(col => col.key !== 'actions').map(column => (
                <div key={column.key} className="mb-3">
                  <label className="form-label" style={{ fontSize: '14px', fontWeight: '500' }}>
                    {column.label}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={`Filter by ${column.label.toLowerCase()}...`}
                    value={filters[column.key] || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, [column.key]: e.target.value }))}
                    style={{ fontFamily: 'var(--font-family)' }}
                  />
                </div>
              ))}
            </div>
            
            <div className="d-flex gap-2 justify-content-end">
              <button 
                className="btn btn-outline-secondary"
                onClick={() => {
                  setFilters({});
                  setShowFilterModal(false);
                }}
                style={{ fontFamily: 'var(--font-family)' }}
              >
                Clear All
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => setShowFilterModal(false)}
                style={{ fontFamily: 'var(--font-family)' }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DataTable;
